import { useState, useEffect } from "react";
import { X, Split, Boxes, RadioTower, User, AlertTriangle, ShieldCheck, Link2, Lock, BookMarked, GitMerge } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function SplitterView({ splitter, onClose, onOpenClient, onRefresh, role }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cables, setCables] = useState([]);
  const [splitters, setSplitters] = useState([]);
  const [form, setForm] = useState({ child_id: "", cable_id: "", fiber: "", parent_port: "" });
  const [saving, setSaving] = useState(false);
  const [linkChild, setLinkChild] = useState("");
  const canEdit = role !== "odczyt";

  const reload = () => {
    api.get(`/splitters/${splitter.id}/ports`).then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get(`/splitters/${splitter.id}/trace`).then((r) => setTrace(r.data)).catch(() => setTrace(null));
  };

  useEffect(() => {
    reload();
    api.get("/cables").then((r) => setCables(r.data)).catch(() => {});
    api.get("/splitters").then((r) => setSplitters(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitter.id]);

  const reservePort = async (port) => {
    const who = window.prompt(t("Zarezerwowano dla (klient/projekt)"));
    if (who === null) return;
    try {
      await api.post("/reservations", { resource_type: "splitter_port", resource_id: splitter.id, detail: String(port), reserved_for: who });
      toast.success(t("Rezerwacja utworzona"));
      reload();
    } catch { toast.error(t("Błąd połączenia")); }
  };

  const connectChildPort = async (port) => {
    if (!linkChild) return;
    try {
      const { data: r } = await api.post(`/splitters/${splitter.id}/connect-child-port`, { child_id: linkChild, parent_port: port });
      toast.success(`${t("Podłączono splitter")} ${r.child_number} → ${t("port")} ${port}${r.pon_port ? ` · PON ${r.pon_port}` : ""}`);
      setLinkChild(""); reload(); onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("Błąd połączenia")); }
  };

  const connectChild = async () => {
    if (!form.child_id || !form.cable_id || !form.fiber) { toast.error(t("Wybierz splitter podrzędny, kabel i włókno")); return; }
    setSaving(true);
    try {
      const { data: r } = await api.post(`/splitters/${splitter.id}/connect-child`, {
        child_id: form.child_id, cable_id: form.cable_id,
        fiber: Number(form.fiber), parent_port: form.parent_port ? Number(form.parent_port) : null,
      });
      toast.success(r.splices_created ? `${t("Podłączono. Utworzono spawów:")} ${r.splices_created} ${t("(mufa nadrzędna i podrzędna) · włókno")} ${form.fiber} ${r.fiber_color || ""}` : t("Podłączono (spawy zaktualizowane)"));
      setForm({ child_id: "", cable_id: "", fiber: "", parent_port: "" });
      reload();
      onRefresh && onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("Błąd połączenia")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4" data-testid="splitter-view">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Split size={18} className="text-orange-400" /> Splitter {splitter.number}</h3>
          <button onClick={onClose} data-testid="splitter-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        {loading || !data ? <div className="p-6 text-slate-500 text-sm">{t("Ładowanie…")}</div> : (
          <div className="overflow-y-auto p-5">
            {/* Summary */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-5 text-sm">
              <span className="flex items-center gap-1.5"><Split size={14} className="text-orange-400" /> {t("Typ")} <b className="font-mono">{data.splitter.split_type}</b></span>
              {data.olt_name && <span className="flex items-center gap-1.5"><RadioTower size={14} className="text-blue-400" /> {data.olt_name} · PON {data.splitter.pon_port || "-"}</span>}
              {data.closure_name && <span className="flex items-center gap-1.5"><Boxes size={14} className="text-purple-400" /> {t("Mufa")} {data.closure_name}</span>}
              <span className="ml-auto flex items-center gap-3">
                <span className="text-green-400">{t("Wolne:")} <b className="font-mono">{data.free}</b></span>
                {data.reserved ? <span className="text-amber-400">{t("Zarezerwowane:")} <b className="font-mono">{data.reserved}</b></span> : null}
                <span className="text-red-400">{t("Zajęte:")} <b className="font-mono">{data.occupied}</b></span>
              </span>
            </div>

            {/* Subordinate (cascade) splitter — input auto-managed by the parent link */}
            {String(data.splitter.input_source || "").startsWith("Splitter") && data.splitter.parent_splitter_id && (
              <div className="mb-4 rounded-lg border border-orange-700/50 bg-orange-950/30 text-orange-200 px-3 py-2 text-xs flex items-center gap-2" data-testid="subordinate-banner">
                <Lock size={13} className="shrink-0" />
                {t("Splitter podrzędny (kaskada) — wejście ustawione automatycznie z nadrzędnego: włókno")} <b className="font-mono">{data.splitter.input_fiber_number ?? "-"}</b>
                {data.splitter.input_fiber_color ? <> ({data.splitter.input_fiber_color})</> : null}. {t("Bez zmiany koloru po drodze.")}
              </div>
            )}

            {/* Optical budget / cascade warning */}
            {trace && (
              <div className={`mb-5 rounded-lg border px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm ${
                trace.budget_status === "krytyczny" ? "bg-red-950/50 border-red-700 text-red-200"
                : trace.budget_status === "ostrzezenie" ? "bg-amber-950/50 border-amber-700 text-amber-200"
                : "bg-emerald-950/40 border-emerald-800 text-emerald-200"}`} data-testid="splitter-budget">
                <span className="font-semibold flex items-center gap-1.5">
                  {trace.budget_status === "ok" ? <ShieldCheck size={16} /> : <AlertTriangle size={16} />}
                  {trace.budget_status === "krytyczny" ? t("Budżet mocy przekroczony!")
                    : trace.budget_status === "ostrzezenie" ? t("Budżet mocy — ostrzeżenie") : t("Budżet mocy OK")}
                </span>
                {trace.cascade_depth > 1 && <span>{t("Kaskada:")} <b className="font-mono">{trace.cascade_depth}× → {trace.effective_split}</b></span>}
                <span>{t("Tłumienie:")} <b className="font-mono">{trace.attenuation_db} dB</b></span>
                <span>Rx: <b className="font-mono">{trace.expected_rx_dbm} dBm</b> (min {trace.min_rx_dbm})</span>
                <span>{t("Zapas:")} <b className="font-mono">{trace.budget_margin_db} dB</b></span>
                {trace.budget_status !== "krytyczny" && (
                  <span className="w-full text-xs opacity-90">
                    {t("Możliwy dalszy podział:")} <b className="font-mono">{trace.max_extra_split}</b>
                    {" · "}{t("dodatkowy zasięg światłowodu:")} <b className="font-mono">~{trace.max_extra_km} km</b>
                  </span>
                )}
              </div>
            )}
            {canEdit && (
              <div className="mb-4 rounded-lg border border-cyan-800/50 bg-cyan-950/20 px-3 py-2 flex flex-wrap items-center gap-2" data-testid="splitter-link-mode">
                <span className="text-xs text-cyan-300 font-semibold flex items-center gap-1.5"><Link2 size={14} /> {t("Podłącz splitter do portu:")}</span>
                <select value={linkChild} onChange={(e) => setLinkChild(e.target.value)} data-testid="splitter-link-child"
                  className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm">
                  <option value="">{t("— wybierz splitter podrzędny —")}</option>
                  {splitters.filter((s) => s.id !== splitter.id).map((s) => <option key={s.id} value={s.id}>{s.number} ({s.split_type})</option>)}
                </select>
                {linkChild
                  ? <span className="text-[11px] text-cyan-200">{t("Kliknij wolny port poniżej — połączenie port↔port bez włókna, dziedziczy OLT/PON.")}</span>
                  : <span className="text-[11px] text-slate-400">{t("Wybierz splitter, potem kliknij wolny port.")}</span>}
                {linkChild ? <button onClick={() => setLinkChild("")} data-testid="splitter-link-cancel" className="text-[11px] text-slate-400 hover:text-white underline">{t("Anuluj")}</button> : null}
              </div>
            )}
            <div className="flex items-stretch gap-6" data-testid="splitter-schematic">
              {/* input */}
              {(() => {
                const casc = String(data.splitter.input_source || "").startsWith("Splitter");
                const spSteps = (trace?.steps || []).filter((s) => s.kind === "splitter");
                const parent = casc && spSteps.length > 1 ? spSteps[spSteps.length - 2] : null;
                return (
                  <div className="flex flex-col items-center justify-center shrink-0" data-testid="splitter-input-node">
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center shadow-lg ${casc ? "bg-orange-600" : "bg-blue-600"}`}>
                      {casc ? <Split size={26} className="text-white" /> : <RadioTower size={26} className="text-white" />}
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-1">{casc ? t("Splitter nadrzędny") : t("Wejście")}</div>
                    <div className="text-xs font-mono text-center">
                      {casc ? (parent?.name || t("kaskada")) : (data.splitter.pon_port || "PON")}
                      {casc && data.splitter.parent_port ? ` · ${t("Port")} ${data.splitter.parent_port}` : ""}
                    </div>
                  </div>
                );
              })()}
              {/* splitter box */}
              <div className="flex flex-col items-center justify-center shrink-0">
                <div className="w-14 h-24 rounded-lg bg-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-white font-heading font-bold text-sm rotate-0">{data.splitter.split_type}</span>
                </div>
              </div>
              {/* output ports grid */}
              <div className="flex-1 grid grid-cols-4 gap-2">
                {data.ports.map((p) => (
                  <button key={p.port}
                    onClick={() => {
                      if (linkChild && !p.occupied && !p.reserved) { connectChildPort(p.port); return; }
                      if (p.occupied) { p.client && onOpenClient && onOpenClient(p.client.id); }
                      else if (!p.reserved) { reservePort(p.port); }
                    }}
                    data-testid={`splitter-port-${p.port}`}
                    title={linkChild && !p.occupied && !p.reserved ? t("Kliknij, aby podłączyć wybrany splitter") : (p.reserved ? `${t("Zarezerwowane:")} ${p.reserved_for || ""}` : (!p.occupied ? t("Zarezerwuj port") : ""))}
                    className={`text-left rounded-md border px-2.5 py-2 transition-colors ${linkChild && !p.occupied && !p.reserved ? "ring-2 ring-cyan-500/60 " : ""}${p.occupied ? "bg-red-900/25 border-red-700/50 hover:bg-red-900/40" : p.reserved ? "bg-amber-900/20 border-amber-600/50" : "bg-green-900/15 border-green-700/40 hover:bg-green-900/30"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-slate-400">{t("Port")} {p.port}</span>
                      <span className={`w-2 h-2 rounded-full ${p.occupied ? "bg-red-400" : p.reserved ? "bg-amber-400" : "bg-green-400"}`} />
                    </div>
                    {p.occupied ? (
                      <div className="mt-1 space-y-0.5 min-w-0">
                        {p.client && (
                          <div className="flex items-center gap-1 min-w-0">
                            <User size={11} className="text-red-300 shrink-0" />
                            <span className="text-xs truncate">{p.client.name}</span>
                          </div>
                        )}
                        {(p.splitters || []).map((sp) => (
                          <div key={sp.id} className="flex items-center gap-1 min-w-0" title={`Splitter ${sp.number} (${sp.split_type})`}>
                            <Split size={11} className="text-orange-300 shrink-0" />
                            <span className="text-xs truncate">{sp.number} <span className="text-slate-500">{sp.split_type}</span></span>
                          </div>
                        ))}
                        {(p.splices || []).map((sp, idx) => (
                          <div key={sp.splice_id || idx} className="flex items-center gap-1 min-w-0" title={`Rozpływ: kabel ${sp.cable_number || ""} wł. ${sp.fiber || ""}`}>
                            <GitMerge size={11} className="text-cyan-300 shrink-0" />
                            <span className="text-xs truncate">{sp.cable_number || t("kabel")} <span className="text-slate-500">wł. {sp.fiber}</span></span>
                          </div>
                        ))}
                      </div>
                    ) : p.reserved ? (
                      <div className="mt-1 flex items-center gap-1 min-w-0">
                        <BookMarked size={11} className="text-amber-300 shrink-0" />
                        <span className="text-xs text-amber-300 truncate">{p.reserved_for || t("rezerw.")}</span>
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-green-400">{t("Wolny")}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Connect a subordinate (cascade) splitter to this splitter's output */}
            {canEdit && (
              <div className="mt-5 pt-4 border-t border-slate-700" data-testid="splitter-connect-child">
                <div className="text-sm font-heading font-bold text-orange-300 mb-1 flex items-center gap-2"><Link2 size={15} /> {t("Wyjście do splittera podrzędnego (kaskada)")}</div>
                <p className="text-[11px] text-slate-400 mb-3">{t("Wskaż kabel wyjściowy i włókno prowadzące do splittera podrzędnego. Spaw na kablu zostanie dodany automatycznie, a trasa sygnału pokaże pełną kaskadę.")}</p>
                <div className="grid grid-cols-2 gap-2">
                  <select value={form.child_id} onChange={(e) => setForm((f) => ({ ...f, child_id: e.target.value }))}
                    data-testid="cc-child" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                    <option value="">{t("— splitter podrzędny —")}</option>
                    {splitters.filter((s) => s.id !== splitter.id).map((s) => <option key={s.id} value={s.id}>{s.number} ({s.split_type})</option>)}
                  </select>
                  <select value={form.cable_id} onChange={(e) => setForm((f) => ({ ...f, cable_id: e.target.value }))}
                    data-testid="cc-cable" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm">
                    <option value="">{t("— kabel wyjściowy —")}</option>
                    {cables.map((c) => <option key={c.id} value={c.id}>{c.number} ({c.fiber_count || "-"} wł.)</option>)}
                  </select>
                  <input type="number" min="1" value={form.fiber} onChange={(e) => setForm((f) => ({ ...f, fiber: e.target.value }))}
                    placeholder={t("Nr włókna")} data-testid="cc-fiber" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                  <input type="number" min="1" value={form.parent_port} onChange={(e) => setForm((f) => ({ ...f, parent_port: e.target.value }))}
                    placeholder={t("Port tego splittera (opcj.)")} data-testid="cc-port" className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm" />
                </div>
                <button onClick={connectChild} disabled={saving} data-testid="cc-submit"
                  className="mt-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white rounded px-3 py-1.5 text-sm flex items-center gap-1.5">
                  <Link2 size={14} /> {saving ? t("Łączenie…") : t("Podłącz i dodaj spaw")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
